import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ExportFormat } from '@/lib/export';

interface ExportButtonsProps {
  onExport: (format: ExportFormat) => void;
  /** Human-readable labels for the active filters (empty = unfiltered). */
  filters?: string[];
}

const FORMATS: Array<{ format: ExportFormat; label: string }> = [
  { format: 'csv', label: 'CSV' },
  { format: 'xls', label: 'Excel' },
];

/**
 * CSV + Excel export buttons. When a filter is active they carry a pulsing
 * badge and a tooltip explaining the download is limited to the filtered rows.
 */
export function ExportButtons({ onExport, filters = [] }: ExportButtonsProps) {
  const filtered = filters.length > 0;

  return (
    <div className="flex items-center gap-2">
      {FORMATS.map(({ format, label }) => {
        const button = (
          <Button
            key={format}
            type="button"
            size="sm"
            variant="outline"
            className="relative"
            onClick={() => onExport(format)}
          >
            <Download />
            {label}
            {filtered && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 size-2.5 animate-pulse rounded-full bg-yellow-400 ring-2 ring-background"
              />
            )}
          </Button>
        );

        if (!filtered) return button;

        return (
          <Tooltip key={format}>
            <TooltipTrigger asChild>
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Exports filtered results only ({filters.join(', ')})
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
